import type { TFunctionNonStrict } from 'i18next';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { RangeSlider } from '../../../../../../components/range-slider';
import { Currency } from '../../../../../../entity/api/organization-billing';
import { i18n, tNamespace } from '../../../../../../services/translation';
import { formatCurrency } from '../../../../../../shared/currency';

const t = tNamespace('components.buy-credits-modal');

type BuyCreditsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pricePerCredit: number;
  currency?: Currency;
  organizationName: string;
  onBuyCredits?: (credits: number, totalPrice: number) => void | Promise<void>;
};

export function BuyCreditsModal(props: BuyCreditsModalProps) {
  const {
    open,
    onOpenChange,
    pricePerCredit,
    currency = Currency.Usd,
    organizationName,
    onBuyCredits,
  } = props;

  const [selectedCredits, setSelectedCredits] = useState(12);

  const totalPrice = selectedCredits * pricePerCredit;
  const rewritesCount = selectedCredits * 2;

  function handleCancel() {
    onOpenChange(false);
  }

  function handleBuyCredits() {
    if (onBuyCredits != null) {
      void onBuyCredits(selectedCredits, totalPrice);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="w-[600px] max-w-full">
        <div className="flex flex-col">
          {/* Title */}
          <DialogTitle align="center">{t('title')}</DialogTitle>

          {/* Description */}
          <DialogDescription align="center" className="mb-8 gap-2 text-dark-gray">
            <Trans
              t={t as unknown as TFunctionNonStrict}
              i18nKey="subtitle"
              values={{ organizationName }}
              components={{ strong: <strong /> }}
            />
            {t('oneTimePurchase')}
          </DialogDescription>

          {/* Credits Display */}
          <div className="flex flex-col gap-1">
            <div className="flex items-end justify-between">
              <h2 className="font-display-1 text-branding-accent-600">
                {t('credits', { count: selectedCredits })}
              </h2>
              <div className="flex flex-col items-end">
                <span className="font-body-3-m text-branding-gray">{t('total')}</span>
                <span className="font-display-1 text-dark-gray">
                  {formatCurrency(totalPrice, currency, i18n.language)}
                </span>
              </div>
            </div>

            {/* Breakdown */}
            <p className="font-body-2-m flex items-center gap-1 text-branding-gray">
              {t('contentGeneration', { count: selectedCredits })}
              <span className="font-body-2-sb text-branding-accent">{t('or')}</span>
              {t('rewrites', { count: rewritesCount })}
            </p>
          </div>

          {/* Range Slider */}
          <RangeSlider
            value={selectedCredits}
            onChange={function (value) {
              setSelectedCredits(value);
            }}
            min={1}
            max={100}
            step={1}
            className="mt-4"
          />
          <div className="my-8 h-px w-full bg-branding-gray-200" />

          {/* Conversion Info */}
          <p className="font-body-3-r text-branding-gray">{t('conversionInfo')}</p>

          {/* Action Buttons */}
          <DialogFooter>
            <Button size="xl" variant="outline" className="flex-1" onClick={handleCancel}>
              {t('cancel')}
            </Button>
            <Button size="xl" variant="accent" className="flex-1" onClick={handleBuyCredits}>
              {t('buyCreditsFor', {
                price: formatCurrency(totalPrice, currency, i18n.language),
              })}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
